<template>
    <div
        data-dialog="true"
        id="printOrderDialog"
        class="flex flex-col relative w-[885px] print:w-full h-full bg-gray-tier-3"
    >
        <header class="sticky top-0 bg-gray-tier-2 border-b border-keyline-primary">
            <div class="w-full flex items-center justify-between py-2 px-6 print:px-0">
                
                <div class="flex items-center gap-4">
                    <h2>
                        {{ $t("Order #") }}
                        <span class="font-medium">{{ data }}</span>
                    </h2>

                    <span v-if="order && Object.keys(order).length > 0" class="font-medium text-secondary flex items-center">
                        <span 
                            v-if="order.record_type !== 'Q'" class="text-[.8rem] uppercase py-0.5 px-3 rounded-full inline-block"
                            :class="backgroundColorSourcing"
                        >
                            {{ order.status }}
                        </span>
                    </span>
                </div>

                <span class="print:hidden flex items-center justify-end gap-2">
                    <AppButton
                        v-if="isEmployee()"
                        @click.prevent="
                            toggleModal(AuditModal, $event, {
                                order_id: order.id,
                            })
                        "
                        :text="$t('Audit')"
                        icon="table-rows-hollow"
                        :disabled="inert"
                    />
                    <AppButton
                        @click="
                            router.post(
                                route(
                                    'web.account.sales.orders.download',
                                    order.id
                                )
                            )
                        "
                        text="Download"
                        icon="export-file"
                        :disabled="inert"
                        :loading="inert"
                    />
                    <DialogCloseButton />
                </span>
            </div>
        </header>
        <div 
            v-if="order && Object.keys(order).length > 0" 
            class="overflow-y-auto mt-[49px] relative"
            :class="scrollClasses"
            @scroll="handleScroll"
        >
            <RecordHeader :record="order" class="px-6 print:px-0 py-3 !fixed top-[51px] w-full type-secondary border-b border-keyline-primary bg-gray-tier-2" />
            <Divider v-if="!settings.is_sourcing_mode" />
            <OrderRecord
                :order="order"
                :invoiceColumns="invoiceColumns"
                :invoiceRows="invoiceRows"
                :lineColumns="lineColumns"
                :lineRows="lineRows"
                :shipmentColumns="shipmentColumns"
                :shipmentRows="shipmentRows"
                :notesColumns="notesColumns"
                :salesData="salesData"
                fromDialog="true"
                class="p-6 print:px-0 mb-8"
            />
        </div>
        <div v-else-if="errors || (!order && !isLoading)" class="grow p-8 print:px-0 flex items-center justify-center">
            <RecordsNotFound
                icon="icon-error-outline"
                header="Order Not Found"
                message="Please contact support through live chat if the problem persists."
            />
        </div>
        <div v-else class="absolute inset-0 flex items-center justify-center">
            <LoadingSpinner :large="true" />
        </div>
    </div>
</template>
<script setup>
import { computed, inject, markRaw, onMounted, ref } from "vue";
import { useModalDialog, useUser, useScroll, useStatus } from "@/Composables";
import axios from "axios";
import {
    AppButton,
    AuditModal,
    DateFormat,
    DialogCloseButton,
    Divider,
    LoadingSpinner,
    MoneyFormat, OrderLineDetail,
    OrderRecord,
    OrderTracking,
    RecordHeader,
    RecordsNotFound,
    TableDownloadLink,
    TableStatus,
} from "@/Components";
import { router } from "@inertiajs/vue3";

const route = inject("route");
const props = defineProps(["data", "cards"]);

const { isEmployee } = useUser();
const { toggleModal } = useModalDialog();
const { scrollClasses, handleScroll } = useScroll();
const { getStatusColors } = useStatus();
const { settings } = useUser();

let backgroundColorSourcing = computed(() => getStatusColors(order.value?.status));

const isLoading = ref(true);
const order = ref();
const cards = ref({});
const salesData = ref(null);
const errors = ref(false);

onMounted(() => {
    axios
        .get(route("api.v1.private.insights.orders.show", props.data), {
            params: {
                with: ["order", "cards", "salesData"],
            },
        })
        .then((response) => {
            order.value = response.data["order"];
            cards.value = response.data["cards"] || {};
            salesData.value = response.data["salesData"] || null;
            order.value.notes.data = order.value.notes;
            isLoading.value = false;
        })
        .catch(() => (errors.value = true));
});

const invoiceColumns = [
    {
        name: "Date",
        path: "date",
        component: markRaw(DateFormat),
        props: (row) => {
            return {
                date: row.date,
                class: 'type-tertiary',
            };
        },
    },
    {
        name: "Inv #",
        component: markRaw(TableDownloadLink),
        props: (row) => ({
            linkText: row.invoice_id,
            svg: 'FileDownload',
            svgSide: 'right',
            href: route('web.account.sales.invoices.show', {
                invoice: row.invoice_id,
                sequence: row.sequence,
            }),
        }),
    },
    {
        name: "Due Date",
        tdClasses: "type-tertiary",
        component: markRaw(DateFormat),
        props: (row) => ({
            date: row.due_date,
        }),
    },
    {
        name: "Status",
        component: TableStatus,
        props: (row) => ({
            value: row.accounts_receivable?.opn_balance > 0 ? "Due" : "Paid",
        }),
    },
    {
        name: "Total",
        component: markRaw(MoneyFormat),
        props: (row) => {
            return {
                amount: row.accounts_receivable?.orig_balance,
                alignRight: true,
            };
        },
        alignRight: true,
    },
    {
        name: "Due",
        component: markRaw(MoneyFormat),
        headClass: '!pr-6',
        cellClass: '!pr-6 text-right font-medium',
        props: (row) => {
            return {
                amount: row.accounts_receivable?.opn_balance,
                alignRight: true,
                cellClass: row.accounts_receivable?.opn ?? 'text-orange',
            };
        },
        alignRight: true,
    },
];
const lineColumns = [
    {
        name: "#",
        path: "line_number",
    },
    {
        name: "Item",
        component: markRaw(OrderLineDetail),
        props: row => ({
            linkText: row.item_id,
            routeName: 'web.catalog.products.show',
            routeParams: row.slug,
            line: row,
            card: cards.value?.[row.item_id],
            showManufacturer: true,
            showDescription: true,
        }),
        noWrap: false
    },
    {
        name: "Qty",
        path: "quantity_ordered",
    },
    {
        name: "Shp",
        path: "quantity_shipped",
    },
    {
        name: "BO",
        path: "quantity_backordered",
    },
    {
        name: "GP%",
        path: "gross_profit",
        cellClass: 'type-tertiary',
    },
    {
        name: "Cost",
        path: "cost",
        component: markRaw(MoneyFormat),
        props: (row) => {
            return {
                amount: row.cost,
                stripFormatting: true,
                alignRight: true,
            };
        },
        alignRight: true,
    },
    {
        name: "Price",
        component: markRaw(MoneyFormat),
        props: (row) => ({
            amount: row.price,
            stripFormatting: true,
            alignRight: true,
        }),
        alignRight: true,
    },
    {
        name: "Surcharge",
        component: markRaw(MoneyFormat),
        props: (row) => ({
            amount: row.surcharge,
            stripFormatting: true,
            alignRight: true,
        }),
        alignRight: true,
    },
    {
        name: "Ext",
        component: markRaw(MoneyFormat),
        headClass: '!pr-6',
        props: (row) => ({
            amount: row.subtotal,
            alignRight: true,
            stripFormatting: true,
            cellClass: 'pr-4',
        }),
        alignRight: true,
    },
];

const shipmentColumns = [
    {
        name: "Shipped",
        path: "enter_date",
        component: markRaw(DateFormat),
        props: (row) => {
            return {
                date: row.date,
                class: 'type-tertiary',
            };
        },
    },
    {
        name: "Tracking #",
        component: markRaw(OrderTracking),
        props: (row) => ({
            url: row.tracking_link,
            text: row.tracking_number,
            shipping_method: row.service_type,
        }),
    },
    {
        name: "# Items",
        path: "total_quantity_shipped",
        props: (row) => {
            return {
                quantity: row.total_quantity_shipped,
            };
        },
    },
    {
        name: "Service",
        path: "service_type",
    },
    {
        name: "Ship Via Code",
        path: "ship_via_code",
        alignRight: true,
        headClass: '!pr-6',
        cellClass: '!pr-6 text-right',
    },
];

const notesColumns = [
    {
        name: "Enter Date",
        path: "enter_date",
        component: markRaw(DateFormat),
        props: (row) => {
            return {
                date: row.enter_date,
                class: 'type-tertiary leading-normal',
            };
        },
        vAlign: 'top',
    },
    {
        name: "Entered By",
        path: "enter_by",
        cellClass: 'leading-normal',
        vAlign: 'top',
    },
    {
        name: "Note",
        path: "note",
        noWrap: true,
        cellClass: 'leading-normal',
        vAlign: 'top',
    },
];

const lineRows = computed(() => order.value?.lines);
const shipmentRows = computed(() => order.value?.shipments);
const invoiceRows = computed(() => order.value?.invoices);
</script>
